import { Trans, useLingui } from '@lingui/react/macro'
import { trpc } from '$src/lib/trpc'
import { useAuthStore } from '$src/store/useAuthStore'
import {
  Alert,
  Box,
  Center,
  Loader,
  Text,
  useMantineTheme,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { AppPage, ChatRoom, createChatHooks } from '@markku/components'
import { IconInfoCircle } from '@tabler/icons-react'
import { createFileRoute } from '@tanstack/react-router'

function MessagesViewPage() {
  const { t } = useLingui()
  const { id } = Route.useParams()
  const roomId = Number(id)
  const { user } = useAuthStore()
  const theme = useMantineTheme()
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.md})`)

  const chatHooks = createChatHooks(trpc)
  const { data: room } = chatHooks.useGetRoom(roomId)
  const deleteMessage = chatHooks.useDeleteMessage()

  const handleDeleteMessage = (messageId: number) => {
    deleteMessage.mutateAsync({ id: messageId })
  }

  if (!room) {
    return (
      <AppPage title={t`Loading...`} backTarget="/messages">
        <Center h={400}>
          <Loader />
        </Center>
      </AppPage>
    )
  }

  const isCustomerServiceRoom = room.type === 'customer_service'

  const content = (
    <AppPage
      title={room.name}
      subtitle={
        isCustomerServiceRoom ? t`We serve weekdays 8am-4pm` : undefined
      }
      backTarget="/messages"
      disablePadding
      fullHeight
    >
      {isCustomerServiceRoom && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          variant="light"
          styles={{
            root: { borderRadius: 0, borderLeft: 0, borderRight: 0 },
          }}
        >
          <Text size="sm" fw={500}>
            <Trans>
              This is a shared conversation - all members of your household can
              read and send messages
            </Trans>
          </Text>
        </Alert>
      )}
      <ChatRoom
        roomId={roomId}
        currentUserId={user?.id || 0}
        chatHooks={chatHooks}
        onDeleteMessage={handleDeleteMessage}
      />
    </AppPage>
  )

  // On desktop, wrap in full-height container since parent layout uses natural scrolling
  if (isDesktop) {
    return (
      <Box
        style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}
      >
        {content}
      </Box>
    )
  }

  return content
}

export const Route = createFileRoute('/_app/messages/$id')({
  component: MessagesViewPage,
})
